<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
    overlay-opacity="0.92"
    content-class="modern-agency-dialog"
    z-index="3000"
    transition="dialog-bottom-transition"
  >
    <v-card
      v-if="donation"
      class="modern-transfer-card"
      elevation="0"
      rounded="xl"
    >
      <!-- Animated Background Elements -->
      <div class="background-animation">
        <div class="floating-circle circle-1"></div>
        <div class="floating-circle circle-2"></div>
        <div class="floating-circle circle-3"></div>
      </div>

      <!-- Header with enhanced gradient -->
      <div class="dialog-header">
        <div class="header-content">
          <div class="icon-container">
            <v-icon size="36" color="white" class="transfer-icon"
              >mdi-swap-horizontal</v-icon
            >
            <div class="pulse-ring"></div>
          </div>
          <div class="title-section">
            <h2 class="dialog-title">Transfer Amounts</h2>
            <p class="dialog-subtitle">Secure & Fast Money Transfer</p>
          </div>
        </div>
        <v-btn icon size="small" class="close-btn" @click="onClose">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Content Section -->
      <v-card-text class="content-section">
        <!-- Enhanced Donation Details -->
        <div class="donation-details-container">
          <div class="agency-profile" v-if="donation && donation.agency_id">
            <div class="agency-avatar">
              <v-avatar size="64" class="avatar-enhanced">
                <img
                  :src="
                    donation.agency_id?.midThumbnail ||
                    donation.agency_id?.thumbnail ||
                    ''
                  "
                  :alt="donation.agency_id?.name || 'Agency'"
                  @error="handleImageError"
                />
                <div class="avatar-overlay"></div>
              </v-avatar>
            </div>
            <div class="agency-info">
              <h3 class="agency-name">
                {{ donation.agency_id?.name || "Agency Name" }}
              </h3>
              <p class="agency-email">
                {{ donation.agency_id?.email || "agency@example.com" }}
              </p>
              <div class="trust-indicator">
                <v-icon size="16" color="success">mdi-shield-check</v-icon>
                <span>Verified Partner</span>
              </div>
            </div>
          </div>

          <v-divider class="section-divider"></v-divider>

          <!-- Enhanced Transfer Details -->
          <div class="transfer-details">
            <div class="detail-item highlight-item">
              <div class="detail-label">
                <v-icon size="20" color="primary">mdi-currency-usd</v-icon>
                <span>Total Amount</span>
              </div>
              <div class="detail-value primary-value">
                ${{ formatAmount(donation.total_amount / 100) }}
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-label">
                <v-icon size="20" color="warning">mdi-calculator</v-icon>
                <span>Amount With Charges</span>
              </div>
              <div class="detail-value">
                ${{ formatAmount(donation.with_charges / 100) }}
              </div>
            </div>
            <!-- Enhanced Payment Mode Selection -->
            <div class="payment-mode-section">
              <label class="payment-label">Payment Mode</label>
              <v-select
                v-model="selectedPaymentMode"
                :items="paymentModes"
                item-title="name"
                item-value="value"
                return-object
                outlined
                class="payment-select"
                placeholder="Select payment method"
                :error-messages="!selectedPaymentMode ? 'Payment method is required' : ''"
                @change="closeMenu"
              >
                <template v-slot:selection="{ item }">
                  <div class="payment-option">
                    <v-icon size="20" :color="item.color">{{
                      item.icon
                    }}</v-icon>
                    <span>{{ item.name }}</span>
                  </div>
                </template>
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on" class="payment-item">
                    <v-list-item-icon>
                      <v-icon :color="item.color">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{
                        item.description
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>

              <label class="payment-label">Amount</label>
              <div class="custom-amount">
                <div class="input-container">
                  <span class="currency-symbol">$</span>
                  <input
                    type="number"
                    v-model="amount"
                    placeholder="Enter amount"
                    @input="validateAmount"
                    step="0.01"
                    min="0"
                    :max="maxAmount"
                  />
                  <p v-if="errorMessage" class="error-text">
                    {{ errorMessage }}
                  </p>
                </div>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-label">
                <v-icon size="20" color="info">mdi-wallet</v-icon>
                <span>Balance to Pay</span>
              </div>
              <div class="detail-value">
                ${{ formatAmount(donation.balance / 100) }}
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-label">
                <v-icon size="20" color="success">mdi mdi-check-all</v-icon>
                <span>Paid Amount</span>
              </div>
              <div class="detail-value">
                ${{ formatAmount(donation.paid_by_admin / 100) }}
              </div>
            </div>

            <!-- <div class="detail-item">
              <div class="detail-label">
                <v-icon size="20" color="secondary">mdi-account-supervisor</v-icon>
                <span>Admin Payment</span>
              </div>
              <div class="detail-value">
                <v-chip 
                  :color="donation.paid_by_admin ? 'success' : 'default'"
                  size="small"
                  variant="flat"
                >
                  {{ donation.paid_by_admin ? 'Yes' : 'No' }}
                </v-chip>
              </div>
            </div> -->
          </div>
        </div>

        <!-- Transfer Summary -->
        <div class="transfer-summary">
          <div class="summary-header">
            <h4>Transfer Summary</h4>
            <v-icon color="primary">mdi-file-document-outline</v-icon>
          </div>
          <div class="summary-content">
            <div class="summary-row">
              <span>
                Processing Fee
                <span v-if="commission && commission.commission_percentage">
                  ({{ commission.commission_percentage }}%)
                </span>
              </span>
              <span class="fee-amount">${{ commissionFee() }}</span>
            </div>
            <div class="summary-row total-row">
              <span>Total</span>
              <span class="total-amount"
                >${{ formatAmount(donation.balance / 100) }}</span
              >
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Enhanced Action Buttons -->
      <v-card-actions class="action-section">
        <v-btn
          variant="outlined"
          size="large"
          class="cancel-btn"
          @click="onClose"
          :disabled="loading"
        >
          <v-icon start>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          size="large"
          class="confirm-btn"
          elevation="0"
          @click="handleConfirm"
          :loading="loading"
          :disabled="loading || !selectedPaymentMode || !amount"
        >
          <v-icon start>mdi-check-circle</v-icon>
          {{ loading ? "Processing..." : "Confirm Transfer" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "ModernTransferDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      required: true,
    },
    donation: {
      type: Object,
      default: () => ({
        total_amount: 1000,
        with_charges: 1025,
        balance: 5000,
        paid_by_admin: false,
        agency_id: {
          name: "Sample Agency",
          email: "agency@example.com",
          thumbnail: null,
        },
      }),
    },
  },
  data() {
    return {
      loading: false,
      selectedPaymentMode: null,
      isCustomAmountFocused: false,
      amount: null,
      maxAmount: 0,
      paymentModes: [
        {
          name: "Credit Card",
          value: "credit_card",
          icon: "mdi-credit-card",
          color: "primary",
          description: "Instant transfer",
        },
        {
          name: "Bank Transfer",
          value: "bank_transfer",
          icon: "mdi-bank",
          color: "success",
          description: "1-2 business days",
        },
        {
          name: "Digital Wallet",
          value: "digital_wallet",
          icon: "mdi-wallet",
          color: "info",
          description: "Instant transfer",
        },
        {
          name: "Cash",
          value: "cash",
          icon: "mdi mdi-currency-usd",
          color: "warning",
          description: "Instant transfer",
        },
      ],
      placeholderImage: require("../../../assets/default.jpg"),
      commission: null,
      errorMessage: null,
    };
  },
  computed: {
    balanceInDollars() {
      return this.donation.balance / 100;
    },
  },
  watch: {
  amount() {
    this.validateAmount();
  },
  selectedPaymentMode(newVal) {
    if (!newVal) {
      this.errorMessage = "Please select a payment method";
    } else {
      this.errorMessage = null;
    }
  }
},
  methods: {
    ...mapActions({
      activeCommision: "commission/activeCommision",
      adminPayToAgency: "donation/adminPayToAgency",
    }),
validateAmount() {
  this.errorMessage = null;

  // Check if empty
  if (!this.amount) {
    this.errorMessage = "Please enter an amount";
    return false;
  }

  // Convert to number
  const amount = parseFloat(this.amount);

  // Check if valid number
  if (isNaN(amount)) {
    this.errorMessage = "Please enter a valid amount";
    return false;
  }

  // Check if negative
  if (amount < 0) {
    this.errorMessage = "Amount cannot be negative";
    return false;
  }

  // Check if exceeds balance
  if (amount > this.balanceInDollars) {
    this.errorMessage = `Amount cannot exceed $${this.balanceInDollars.toFixed(2)}`;
    return false;
  }

  // Round to 2 decimal places
  this.amount = amount.toFixed(2);
  return true;
},
    async handleConfirm() {
      if (!this.validateAmount()) {
        return;
      }

      if (!this.selectedPaymentMode) {
        this.$toast?.error("Please select a payment method");
        return;
      }

      this.loading = true;
      try {
        // Convert amount to cents
        const amountInCents = Math.round(parseFloat(this.amount) * 100);

        const payload = {
          payment_mode: this.selectedPaymentMode.value,
          agencyId: this.donation.agency_id._id,
          amount: amountInCents, // Send amount in cents
        };
        await this.adminPayToAgency(payload);
        this.$toast?.success("Transfer confirmed successfully!");
        this.onClose();
      } catch (error) {
        const msg =
          error?.response?.data?.message || error?.message || "Transfer failed";
        this.$toast?.error(msg);
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    formatAmount(amount) {
      return amount ? amount.toLocaleString() : "0";
    },
    formatCents(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    },
    onRoundAmount() {
      try {
        this.isCustomAmountFocused = false;
        this.errorMessage = ""; // clear previous error

        if (!this.amount) return;

        // Convert input to float and round to 2 decimals
        this.amount = parseFloat(this.amount).toFixed(2);

        // Convert both to cents for accurate comparison
        const amountInCents = Math.round(parseFloat(this.amount) * 100);
        const maxAllowedInCents = this.donation.with_charges;

        if (amountInCents > maxAllowedInCents) {
          this.errorMessage =
            "Amount should be equal to or less than with charges";
          this.amount = parseFloat(this.donation.with_charges).toFixed(2);
        }

        // Optional: format and log for reference
        const formattedAmount = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amountInCents / 100);

        console.log("Formatted amount:", formattedAmount);
      } catch (error) {
        console.error("Error rounding amount:", error);
      }
    },
    handleCustomAmountFocus() {
      this.isCustomAmountFocused = true;
      this.amount = null;
    },
    commissionFee() {
      if (
        !this.commission ||
        typeof this.commission.commission_percentage !== "number" ||
        !this.donation ||
        typeof this.donation.balance !== "number"
      ) {
        return "0.00";
      }
      // If total_amount is in cents, convert to dollars
      const totalAmount = this.donation.balance / 100;
      const fee = totalAmount * (this.commission.commission_percentage / 100);
      return fee.toFixed(2);
    },
    calculateFee() {
      console.log(this.donation, "donation data");
      const fee =
        (this.donation.with_charges || 0) - (this.donation.total_amount || 0);
      return fee > 0 ? fee.toLocaleString() : "0";
    },

    handleImageError(e) {
      e.target.src = this.placeholderImage;
    },

    getactiveCommision() {
      this.activeCommision()
        .then((res) => {
          this.commission = res.commission;
        })
        .catch((error) => {
          this.snackbar = true;
          this.showErrorMessage(error);
          console.log(error.response.data.message);
        });
    },
    closeMenu() {
      // This will close the menu after selection
      this.$nextTick(() => {
        const menu = this.$refs.paymentSelect?.$refs.menu;
        if (menu) {
          menu.isActive = false;
        }
      });
    },
  },
  mounted() {
    // Initialize the active commission on mount
    this.getactiveCommision();
    this.maxAmount = this.balanceInDollars;
    // Set initial amount to the balance
    this.amount = this.balanceInDollars.toFixed(2);
  },
};
</script>

<style scoped>
.modern-transfer-card {
  border-radius: 24px !important;
  overflow: hidden;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
}

.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(6, 147, 227, 0.1) 0%,
    rgba(155, 81, 224, 0.1) 100%
  );
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 100px;
  height: 100px;
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.circle-2 {
  width: 60px;
  height: 60px;
  bottom: -30px;
  left: -30px;
  animation-delay: 2s;
}

.circle-3 {
  width: 80px;
  height: 80px;
  top: 50%;
  right: -40px;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.dialog-header {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  padding: 32px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.icon-container {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 16px;
  backdrop-filter: blur(10px);
  position: relative;
}

.transfer-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

.title-section {
  color: white;
}

.dialog-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.dialog-subtitle {
  opacity: 0.9;
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.1);
}

.content-section {
  padding: 40px 32px;
  background: white;
  position: relative;
  z-index: 1;
}

.info-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.info-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.info-icon {
  background: white;
  border-radius: 50%;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-content h4 {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
}

.info-content p {
  margin: 0 0 12px 0;
  color: #64748b;
  line-height: 1.6;
}

.progress-indicator {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  width: 75%;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border-radius: 2px;
  animation: progress 3s ease-in-out infinite;
}

@keyframes progress {
  0% {
    width: 0%;
  }
  100% {
    width: 75%;
  }
}

.security-badges {
  display: flex;
  gap: 8px;
}

.badge {
  padding: 4px 8px;
  background: #10b981;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.donation-details-container {
  background: #f8fafc;
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 32px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.agency-profile {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
}

.agency-avatar {
  position: relative;
}

.avatar-enhanced {
  border: 4px solid white;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(6, 147, 227, 0.1) 0%,
    rgba(155, 81, 224, 0.1) 100%
  );
  border-radius: 50%;
}

.verified-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #10b981;
  border-radius: 50%;
  padding: 4px;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.agency-info {
  flex: 1;
}

.agency-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #1e293b;
}

.agency-email {
  color: #64748b;
  margin: 0 0 12px 0;
  font-size: 1rem;
}

.trust-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #10b981;
  font-weight: 600;
  font-size: 0.9rem;
}

.section-divider {
  margin: 24px 0;
  opacity: 0.3;
}

.transfer-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.transfer-details,
.payment-mode-section {
  overflow: visible !important;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.detail-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.highlight-item {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  color: white;
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
  font-size: 1.1rem;
}

.primary-value {
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
}

.payment-mode-section {
  padding: 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.payment-label {
  display: block;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1e293b;
  font-size: 1rem;
}

.payment-select {
  margin-bottom: 0;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 12px;
}

.payment-item {
  padding: 12px 16px;
  margin: 4px 0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.payment-item:hover {
  background: #f8fafc;
}

.transfer-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.summary-header h4 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
}

.total-row {
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  padding-top: 16px;
  font-weight: 700;
  font-size: 1.1rem;
}

.fee-amount {
  color: #f59e0b;
  font-weight: 600;
}

.total-amount {
  color: #0693e3;
  font-weight: 700;
  font-size: 1.3rem;
}

.action-section {
  padding: 32px;
  background: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.cancel-btn {
  color: #6b7280;
  border-color: #d1d5db;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.confirm-btn {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  padding: 0 32px;
  transition: all 0.3s ease;
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(6, 147, 227, 0.3) !important;
}

.confirm-btn:disabled {
  opacity: 0.7;
  transform: none;
  background: linear-gradient(135deg, #cccccc 0%, #999999 100%) !important;
  cursor: not-allowed;
}
/* Responsive Design */
@media (max-width: 768px) {
  .modern-transfer-card {
    margin: 16px;
    max-width: calc(100vw - 32px) !important;
    border-radius: 16px !important;
  }

  .dialog-header {
    padding: 24px 20px;
  }

  .header-content {
    gap: 16px;
  }

  .dialog-title {
    font-size: 1.4rem;
  }

  .content-section {
    padding: 24px 20px;
  }

  .info-container {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .info-card {
    padding: 20px;
  }

  .donation-details-container {
    padding: 24px;
  }

  .agency-profile {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .agency-profile .agency-info {
    text-align: center;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
  }

  .detail-value {
    align-self: flex-end;
  }

  .action-section {
    padding: 24px 20px;
    flex-direction: column;
    gap: 16px;
  }

  .cancel-btn,
  .confirm-btn {
    width: 100%;
    margin: 0;
  }
}

@media (max-width: 480px) {
  .modern-transfer-card {
    margin: 8px;
    max-width: calc(100vw - 16px) !important;
  }

  .dialog-header {
    padding: 20px 16px;
  }

  .header-content {
    flex-direction: row;
    align-items: center;
  }

  .icon-container {
    padding: 12px;
  }

  .content-section {
    padding: 20px 16px;
  }

  .info-card {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .donation-details-container {
    padding: 20px;
  }

  .transfer-summary {
    padding: 20px;
  }

  .action-section {
    padding: 20px 16px;
  }
}

/* Enhanced Dialog Transitions */
::v-deep .dialog-bottom-transition-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

::v-deep .dialog-bottom-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

::v-deep .dialog-bottom-transition-enter-from {
  transform: translateY(50px) scale(0.9);
  opacity: 0;
}

::v-deep .dialog-bottom-transition-leave-to {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}

/* Loading States */
.confirm-btn.v-btn--loading {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) !important;
}

.confirm-btn.v-btn--loading .v-btn__content {
  color: white !important;
}

/* Custom Scrollbar */
.content-section::-webkit-scrollbar {
  width: 6px;
}

.content-section::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.content-section::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border-radius: 3px;
}

.content-section::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
}

/* Focus States */
.payment-select .v-field--focused {
  border-color: #0693e3;
}

.payment-select .v-field--focused .v-field__outline {
  color: #0693e3;
}

/* Hover Effects for Interactive Elements */
.detail-item.highlight-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(6, 147, 227, 0.3);
}

.agency-avatar:hover .avatar-enhanced {
  transform: scale(1.05);
  transition: transform 0.3s ease;
}

.info-card.processing-card:hover::before {
  background: linear-gradient(135deg, #0693e3 0%, #06b6d4 100%);
}

.info-card.security-card:hover::before {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

/* Enhanced Accessibility */
.close-btn:focus {
  outline: 2px solid rgba(255, 255, 255, 0.5);
  outline-offset: 2px;
}

.confirm-btn:focus {
  outline: 2px solid rgba(6, 147, 227, 0.5);
  outline-offset: 2px;
}

.cancel-btn:focus {
  outline: 2px solid rgba(107, 114, 128, 0.5);
  outline-offset: 2px;
}

/* Dark Mode Support */
/* @media (prefers-color-scheme: dark) {
  .modern-transfer-card {
    background: #1e293b;
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .content-section {
    background: #1e293b;
  }
  
  .donation-details-container {
    background: #334155;
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .info-card {
    background: linear-gradient(135deg, #334155 0%, #475569 100%);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .detail-item {
    background: #334155;
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .transfer-summary {
    background: linear-gradient(135deg, #334155 0%, #475569 100%);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .action-section {
    background: #334155;
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .agency-name {
    color: #f8fafc;
  }
  
  .info-content h4 {
    color: #f8fafc;
  }
  
  .payment-label {
    color: #f8fafc;
  }
  
  .summary-header h4 {
    color: #f8fafc;
  }
} */

/* Print Styles */
@media print {
  .modern-transfer-card {
    box-shadow: none !important;
    border: 1px solid #000;
  }

  .dialog-header {
    background: #000 !important;
    color: #fff !important;
  }

  .confirm-btn,
  .cancel-btn {
    display: none;
  }

  .background-animation {
    display: none;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .floating-circle,
  .transfer-icon,
  .pulse-ring,
  .progress-bar {
    animation: none;
  }

  .info-card:hover,
  .detail-item.highlight-item:hover,
  .confirm-btn:hover {
    transform: none;
  }

  .agency-avatar:hover .avatar-enhanced {
    transform: none;
  }
}

.custom-amount {
  margin-bottom: 24px;
}

.input-container {
  position: relative;
}

.currency-symbol {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #4a5568;
  font-weight: 500;
}

.input-container input {
  width: 100%;
  padding: 16px 16px 16px 40px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: "Poppins", sans-serif;
  transition: all 0.3s ease;
}

.input-container input:focus {
  outline: none;
  border-color: #4a6fa5;
}

.error-text {
  color: red;
  font-size: 0.9rem;
  margin-top: 5px;
}

.underline {
  position: absolute;
  bottom: 0;
  left: 10px;
  right: 0;
  height: 2px;
  background-color: #4a6fa5;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.underline.active {
  transform: scaleX(1);
}

.input-container input {
  width: 100%;
  padding: 16px 16px 16px 40px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
  -moz-appearance: textfield; /* Firefox */
}

/* Hide number input arrows */
.input-container input::-webkit-outer-spin-button,
.input-container input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.input-container input:focus {
  outline: none;
  border-color: #4a6fa5;
  box-shadow: 0 0 0 2px rgba(74, 111, 165, 0.2);
}
</style>